import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';

// ==================== Type Definitions ====================

type MERCADOPAGO_ERROR = unknown;

/**
 * Error returned by MercadoPago SDK during card tokenization or form validation.
 */
export interface MercadoPagoError {
  /** Error code from MercadoPago (e.g., "E301", "316") */
  code?: string;
  /** Error message description */
  message: string;
  /** Field that caused the error (e.g., "cardNumber", "securityCode") */
  field?: string;
}

export interface FieldStyle {
  color?: string;
  fontFamily?: string;
  fontSize?: string;
  fontStyle?: string;
  fontVariant?: string;
  fontWeight?: string;
  height?: string;
  margin?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  marginTop?: string;
  padding?: string;
  paddingBottom?: string;
  paddingLeft?: string;
  paddingRight?: string;
  paddingTop?: string;
  placeholderColor?: string;
  textAlign?: string;
  width?: string;
}

export interface FieldCustomFont {
  src: string;
}

/**
 * Form field configuration for MercadoPago card form.
 */
export interface CardFormFieldConfig {
  /** DOM element ID where the field will be rendered */
  id: string;
  /** Defines field placeholder. */
  placeholder?: string;
  /** Defines field styles. */
  style?: FieldStyle;
  /** Defines field custom fonts. */
  customFonts?: FieldCustomFont[];
  /** Defines the aria-label attribute for screen readers */
  srLabel?: string;
  /** Defines the aria-required attribute for screen readers */
  ariaRequired?: boolean;
}

export interface CardFormCardNumberConfig extends CardFormFieldConfig {
  /** Enables Luhn validation for card number field. */
  enableLuhnValidation?: boolean;
}

export interface CardFormDateConfig extends CardFormFieldConfig {
  /** Mode for expiration date field  */
  mode?: 'short' | 'full';
}

export interface MercadoPagoIdentificationType {
  installments: string;
  installment_rate: number;
  discount_rate: number;
  min_allowed_amount: number;
  max_allowed_amount: number;
  recommended_message: string;
  installment_amount: number;
  total_amount: number;
  payment_method_option_id: string;
}

export interface _MercadoPagoPaymentMethodSetting {
  security_code: {
    mode: string,
    card_location: string,
    length: number
  },
  card_number: {
    length: number,
    validation: string
  },
  bin: {
    pattern: string,
    installments_pattern: string,
    exclusion_pattern: string,
  }
}

export interface MercadoPagoPaymentMethod {
  additional_info_needed: string[],
  agreements?: string[],
  card?: {
    bin: number;
    length: {min: number; max: number};
    security_code: {
      length: number;
      card_location: string;
      mode: string;
    }
    validation: string;
  }
  deferred_capture: string,
  id: string,
  issuer: {
    default: boolean,
    name: string,
    id: number
  }
  labels: string[],
  marketplace: string,
  merchant_account_id?: string,
  name: string,
  payment_type_id: string,
  processing_mode: string,
  settings: _MercadoPagoPaymentMethodSetting[],
  site_id: string,
  status: string,
  thumbnail: string,
  // Apparently legacy fields
  secure_thumbnail?: string,
  min_accreditation_days?: number,
  max_accreditation_days?: number,
  accreditation_time?: number,
}

export interface MercadoPagoIssuer {
  id: string;
  name: string;
  thumbnail: string;
  processing_mode: string;
}

export interface _MercadoPagoInstallmentsPayerCost {
  installments: string,
  installment_rate: number,
  discount_rate: number,
  min_allowed_amount: number,
  max_allowed_amount: number,
  recommended_message: string,
  installment_amount: number,
  total_amount: number,
  payment_method_option_id: string,
}

export interface MercadoPagoInstallmentsResponse {
  merchant_account_id?: string,
  payer_costs: _MercadoPagoInstallmentsPayerCost[]
}

export interface MercadoPagoValidityChange {
  message: string;
  field: string;
}

export interface MercadoPagoCardTokenResponse {
  token: string;
}

export interface MercadoPagoErrorResponse {
  message: string;
}

/**
 * Form configuration for all MercadoPago card form fields.
 */
export interface CardFormConfig {
  /** Form element ID */
  id: string;
  /** Card number field (secure iframe) */
  cardNumber: CardFormCardNumberConfig;
  /** Expiration date field (secure iframe) */
  expirationDate: CardFormDateConfig;
  /** Security code/CVV field (secure iframe) */
  securityCode: CardFormFieldConfig;
  /** Cardholder name field */
  cardholderName: CardFormFieldConfig;
  /** Card issuer/bank field */
  issuer: CardFormFieldConfig;
  /** Installments field */
  installments: CardFormFieldConfig;
  /** Identification type field (e.g., RUT) */
  identificationType: CardFormFieldConfig;
  /** Identification number field */
  identificationNumber: CardFormFieldConfig;
  /** Cardholder email field */
  cardholderEmail: CardFormFieldConfig;
}

/**
 * Callbacks for MercadoPago card form events.
 */
export interface CardFormCallbacks {
  /** Called when the form is mounted to the DOM */
  onFormMounted: (error?: MERCADOPAGO_ERROR) => void;
  /** Called when the form is unmounted from the DOM */
  onFormUnmounted?: (error?: MERCADOPAGO_ERROR) => void;
  /** Callback triggered when getIdentificationTypes() response returns */
  onIdentificationTypesReceived?: (error?: MERCADOPAGO_ERROR, data?: MercadoPagoIdentificationType[]) => void;
  /** Called when payment methods are identified */
  onPaymentMethodsReceived?: (error?: MERCADOPAGO_ERROR, data?: MercadoPagoPaymentMethod[]) => void;
  /** Called when card issuers are received */
  onIssuersReceived?: (error?: MERCADOPAGO_ERROR, data?: MercadoPagoIssuer[]) => void;
  /** Called when installments are received */
  onInstallmentsReceived?: (error?: MERCADOPAGO_ERROR, data?: MercadoPagoInstallmentsResponse) => void;
  /** Called when card token is received or error occurs */
  onCardTokenReceived?: (error?: MercadoPagoError[] | MercadoPagoError | null, data?: MercadoPagoCardTokenResponse) => void;
  /** Called when a resource is being fetched */
  onFetching?: (resource?: string) => void;
  /** Called when the form is submitted */
  onSubmit?: (event?: Event) => void;
  /** Called when the form is fully ready */
  onReady?: () => void;
  /** Called when field validity changes */
  onValidityChange?: (error?: MercadoPagoValidityChange[], field?: string) => void;
  /** Called when card BIN (first 6-8 digits) changes */
  onBinChange?: (bin: string) => void;
  /** Called when an error occurs during form processing */
  onError?: (error?: MercadoPagoErrorResponse[], event?: Event) => void;
}

/**
 * Options for creating a MercadoPago card form.
 */
export interface CardFormOptions {
  /** Whether to auto-mount the form */
  autoMount?: boolean;
  /** Amount for the transaction (used for validation) */
  amount: string;
  /** Whether to use secure iframes for card fields */
  iframe: boolean;
  /** Form field configuration */
  form: CardFormConfig;
  /** Event callbacks */
  callbacks: CardFormCallbacks;
}

/**
 * Data returned from cardForm.getCardFormData().
 */
export interface CardFormData {
  /** Generated card token (single-use) */
  token: string;
  /** Card issuer ID */
  issuerId: string;
  /** Payment method ID (e.g., "visa", "mastercard") */
  paymentMethodId: string;
  /** Transaction amount */
  amount: string;
  /** Number of installments */
  installments: number;
  /** Identification type (e.g., "RUT") */
  identificationType: string;
  /** Identification number */
  identificationNumber: string;
}

/**
 * MercadoPago card form instance with lifecycle methods.
 */
export interface CardFormInstance {
  /** Mounts the form to the DOM */
  mount: () => void;
  /** Unmounts the form from the DOM */
  unmount: () => void;
  /** Gets the collected card form data */
  getCardFormData: () => CardFormData;
}

/**
 * MercadoPago SDK instance.
 */
export interface MercadoPagoSDK {
  /** Creates a new card form instance */
  cardForm: (options: CardFormOptions) => CardFormInstance;
}

/**
 * MercadoPago SDK constructor type.
 */
export interface MercadoPagoConstructor {
  new (publicKey: string, options: { locale: string }): MercadoPagoSDK;
}

/**
 * Global window extensions for MercadoPago.
 */
declare global {
  interface Window {
    /** MercadoPago SDK constructor */
    MercadoPago?: MercadoPagoConstructor;
    /** Device session ID for fraud prevention (set by security.js) */
    MP_DEVICE_SESSION_ID?: string;
  }
}

// ==================== Constants ====================

/**
 * MercadoPago SDK script URL.
 */
const MERCADOPAGO_SDK_URL = 'https://sdk.mercadopago.com/js/v2';

/**
 * MercadoPago device-fingerprinting script, which populates `window.MP_DEVICE_SESSION_ID`.
 *
 * Loaded on demand from `loadSecurityScript()`, never from `index.html`. Fingerprinting only the
 * flows that submit a payment is what keeps it genuinely "strictly necessary for fraud prevention"
 * and so exempt from a consent ask; fingerprinting every visitor on every page — anonymous catalog
 * browsers included — would not be. Two flows qualify: a card form, and a signed-in user's store
 * page, where opening a cooler charges the card. Do not widen it past those without answering the
 * consent question again.
 */
const MERCADOPAGO_SECURITY_URL = 'https://www.mercadopago.com/v2/security.js';

/** How long to wait for MP to populate the device session id before giving up on it. */
const SECURITY_SCRIPT_TIMEOUT_MS = 3000;
const SECURITY_SCRIPT_POLL_MS = 100;

/**
 * User-friendly error messages for MercadoPago error codes.
 * Maps error codes to Spanish messages for display to users.
 */
const MERCADOPAGO_ERROR_MESSAGES: { [key: string]: string } = {
  '101': 'Tu usuario ya está registrado en nuestra plataforma.',
  '106': 'Tu tarjeta no permite realizar pagos a otros países.',
  '109': 'Elige otra tarjeta. Tu tarjeta no acepta pagos en una cuota.',
  '126': 'No pudimos procesar tu pago.',
  '129': 'Elige otra tarjeta. Tu tarjeta no acepta pagos por el monto de validación.',
  '145': 'Una de las partes con la que intentas hacer el pago es de prueba y la otra es usuario real.',
  '150': 'No puedes realizar pagos.',
  '151': 'No puedes realizar pagos.',
  '160': 'No pudimos procesar tu pago.',
  '204': 'Tu tarjeta no esta disponible en estos momentos. Elige otra tarjeta u otro medio de pago.',
  '801': 'Intenta de nuevo en unos minutos.',
  '205': 'Ingresa el número de tu tarjeta.',
  '208': 'Elige un mes.',
  '209': 'Elige un año.',
  '212': 'Ingresa tu tipo de documento.',
  '213': 'Ingresa tu RUT.',
  '214': 'Ingresa tu RUT.',
  '220': 'Ingresa tu banco.',
  '221': 'Ingresa el nombre y apellido.',
  '224': 'Ingresa el código de seguridad.',
  'E203': 'Revisa el código de seguridad.',
  'E301': 'Ingresa un número de tarjeta válido.',
  '316': 'Ingresa un nombre válido.',
  '322': 'El tipo de documento es inválido.',
  '323': 'Revisa tu documento.',
  '324': 'El documento es inválido.',
  '325': 'El mes es inválido.',
  '326': 'El año es inválido.',
  '3034': 'Ingresa un número de tarjeta válido.',
};

/**
 * Default fallback error message when error code is not recognized.
 */
const DEFAULT_ERROR_MESSAGE = 'No pudimos registrar la tarjeta. Revisa que los datos sean los correctos.';

/**
 * Field-specific error messages for validation errors without error codes.
 */
const FIELD_ERROR_MESSAGES: { [key: string]: string } = {
  cardNumber: 'Ingresa un número de tarjeta válido.',
  securityCode: 'Ingresa un código de seguridad válido.',
  expirationDate: 'Ingresa una fecha de expiración válida.',
};

// ==================== Service ====================

/**
 * Service to handle MercadoPago SDK script loading and interaction.
 * Encapsulates all MercadoPago-related functionality and provides type-safe access.
 *
 * @example
 * ```typescript
 * // In your component
 * constructor(private mercadoPagoService: MercadoPagoService) {}
 *
 * async ngOnInit() {
 *   const sdk = await this.mercadoPagoService.loadScript();
 *   const cardForm = this.mercadoPagoService.createCardForm({
 *     amount: '50',
 *     iframe: true,
 *     form: { ... },
 *     callbacks: { ... }
 *   });
 *   cardForm.mount();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class MercadoPagoService {
  private renderer: Renderer2;
  private scriptLoaded = false;
  private scriptLoading = false;
  private loadPromise: Promise<MercadoPagoSDK> | null = null;
  private sdkInstance: MercadoPagoSDK | null = null;
  private securityPromise: Promise<string> | null = null;

  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    private translate: TranslateService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  // ==================== Script Loading ====================

  /**
   * Loads the MercadoPago SDK script dynamically.
   * Returns a promise that resolves with the SDK instance.
   * Handles duplicate load requests by returning the same promise.
   *
   * @returns Promise that resolves with the MercadoPago SDK instance
   * @throws Error if the script fails to load
   */
  loadScript(): Promise<MercadoPagoSDK> {
    // If already loaded, return resolved promise with SDK instance
    if (this.scriptLoaded && this.sdkInstance) {
      return Promise.resolve(this.sdkInstance);
    }

    // If currently loading, return existing promise
    if (this.scriptLoading && this.loadPromise) {
      return this.loadPromise;
    }

    this.scriptLoading = true;

    this.loadPromise = new Promise<MercadoPagoSDK>((resolve, reject) => {
      const script = this.renderer.createElement('script') as HTMLScriptElement;
      script.type = 'text/javascript';
      script.src = MERCADOPAGO_SDK_URL;
      script.async = true;

      script.onload = () => {
        this.scriptLoaded = true;
        this.scriptLoading = false;

        if (window.MercadoPago) {
          // Initialize SDK with public key from environment
          this.sdkInstance = new window.MercadoPago(environment.mp_public_key, {
            locale: 'es-CL',
          });
          resolve(this.sdkInstance);
        } else {
          reject(new Error('MercadoPago SDK loaded but not available on window'));
        }
      };

      script.onerror = () => {
        this.scriptLoading = false;
        this.loadPromise = null;
        reject(new Error('Failed to load MercadoPago SDK script'));
      };

      this.renderer.appendChild(this.document.body, script);
    });

    return this.loadPromise;
  }

  /**
   * Checks if the MercadoPago SDK is loaded and available.
   *
   * @returns true if SDK is loaded and ready to use
   */
  isLoaded(): boolean {
    return this.scriptLoaded && this.sdkInstance !== null;
  }

  /**
   * Gets the MercadoPago SDK instance if available.
   *
   * @returns SDK instance or undefined if not loaded
   */
  getSDK(): MercadoPagoSDK | undefined {
    return this.sdkInstance ?? undefined;
  }

  // ==================== Card Form Management ====================

  /**
   * Gets the default form configuration for MercadoPago card forms.
   * This includes standard Chilean form fields with default placeholders.
   * All field IDs follow the pattern: form-checkout__fieldName
   *
   * @param formId - The form element ID (default: "form-checkout")
   * @returns Complete form configuration with all required fields
   *
   * @example
   * ```typescript
   * const formConfig = this.mercadoPagoService.getDefaultFormConfig();
   * // Use formConfig directly or merge with custom overrides
   * const cardForm = this.mercadoPagoService.createCardForm({
   *   amount: '50',
   *   iframe: true,
   *   form: formConfig,
   *   callbacks: { ... }
   * });
   * ```
   */
  getDefaultFormConfig(formId: string = 'form-checkout'): CardFormConfig {
    const t = (key: string) => this.translate.instant(`components.card_form.${key}`);
    return {
      id: formId,
      cardNumber: {
        id: `${formId}__cardNumber`,
        placeholder: t('card_number_placeholder'),
      },
      expirationDate: {
        id: `${formId}__expirationDate`,
        placeholder: t('expiry_date_placeholder'),
        mode: 'short',
      },
      securityCode: {
        id: `${formId}__securityCode`,
        placeholder: t('cvv_placeholder'),
      },
      cardholderName: {
        id: `${formId}__cardholderName`,
        placeholder: t('full_name_placeholder'),
      },
      issuer: {
        id: `${formId}__issuer`,
        placeholder: t('issuer_placeholder'),
      },
      installments: {
        id: `${formId}__installments`,
        placeholder: t('installments_placeholder'),
      },
      identificationType: {
        id: `${formId}__identificationType`,
        placeholder: t('document_type_placeholder'),
      },
      identificationNumber: {
        id: `${formId}__identificationNumber`,
        placeholder: t('document_rut_placeholder'),
      },
      cardholderEmail: {
        id: `${formId}__cardholderEmail`,
        placeholder: t('email_placeholder'),
      },
    };
  }

  /**
   * Retrieves the cardholder name value from the DOM input element.
   * This is a utility method to avoid direct DOM manipulation in components.
   *
   * @param fieldId - The DOM element ID of the cardholder name input (default: "form-checkout__cardholderName")
   * @returns The cardholder name value, or empty string if element not found
   *
   * @example
   * ```typescript
   * const cardholderName = this.mercadoPagoService.getCardholderNameValue();
   * ```
   */
  getCardholderNameValue(fieldId: string = 'form-checkout__cardholderName'): string {
    const element = this.document.getElementById(fieldId) as HTMLInputElement;
    return element?.value ?? '';
  }

  /**
   * Creates a card form with default configuration and custom callbacks.
   * This is a convenience method that combines getDefaultFormConfig() with createCardForm().
   *
   * @param amount - Transaction amount for validation
   * @param callbacks - Event callbacks for form lifecycle
   * @param options - Optional overrides for form configuration
   * @returns CardFormInstance for managing the form lifecycle
   * @throws Error if SDK is not loaded
   *
   * @example
   * ```typescript
   * // Simple usage with defaults
   * this.cardForm = this.mercadoPagoService.createCardFormWithDefaults(
   *   '50',
   *   {
   *     onReady: () => this.handleReady(),
   *     onSubmit: (event) => this.handleSubmit(event)
   *   }
   * );
   *
   * // With custom form ID
   * this.cardForm = this.mercadoPagoService.createCardFormWithDefaults(
   *   '100',
   *   { ... },
   *   { formId: 'custom-form' }
   * );
   * ```
   */
  createCardFormWithDefaults(
    amount: string,
    callbacks: CardFormCallbacks,
    options?: {
      formId?: string;
      autoMount?: boolean;
      formConfigOverrides?: Partial<CardFormConfig>;
    }
  ): CardFormInstance {
    const formId = options?.formId ?? 'form-checkout';
    const defaultFormConfig = this.getDefaultFormConfig(formId);

    // Merge default config with any overrides
    const formConfig: CardFormConfig = {
      ...defaultFormConfig,
      ...options?.formConfigOverrides,
    };

    return this.createCardForm({
      autoMount: options?.autoMount ?? false,
      amount,
      iframe: true,
      form: formConfig,
      callbacks,
    });
  }

  /**
   * Creates a new MercadoPago card form instance.
   * The SDK must be loaded before calling this method.
   *
   * @param options - Card form configuration options
   * @returns CardFormInstance for managing the form lifecycle
   * @throws Error if SDK is not loaded
   */
  createCardForm(options: CardFormOptions): CardFormInstance {
    if (!this.sdkInstance) {
      throw new Error('MercadoPago SDK not loaded. Call loadScript() first.');
    }
    return this.sdkInstance.cardForm(options);
  }

  /**
   * Mounts a card form to the DOM.
   *
   * @param cardForm - Card form instance to mount
   */
  mountCardForm(cardForm: CardFormInstance): void {
    cardForm.mount();
  }

  /**
   * Unmounts a card form from the DOM.
   * Should be called when the component is destroyed.
   *
   * @param cardForm - Card form instance to unmount
   */
  unmountCardForm(cardForm: CardFormInstance): void {
    if (cardForm) {
      cardForm.unmount();
    }
  }

  // ==================== Device Session ====================

  /**
   * Loads MercadoPago's device-fingerprinting script for the payment flows.
   *
   * Call this when a payment flow is about to be entered — not at bootstrap, and never for an
   * anonymous visitor; `MERCADOPAGO_SECURITY_URL` states which flows qualify and why the list is
   * closed. Call it early within one: MP populates the global asynchronously, so a load started at
   * the moment `getDeviceSessionId()` is read hands back an empty string. Resolves once
   * `window.MP_DEVICE_SESSION_ID` is populated, or after `SECURITY_SCRIPT_TIMEOUT_MS` if MP never
   * sets it; a missing fingerprint degrades the fraud signal but must not block a payment. Repeat
   * calls share one promise and load the script once.
   *
   * Do not add MP's `output="deviceId"` attribute: it redirects the fingerprint to
   * `window.deviceId`, while `getDeviceSessionId()` reads `MP_DEVICE_SESSION_ID`, so the value
   * silently never reaches the payment request.
   *
   * @returns Promise resolving with the device session ID, or an empty string if unavailable
   */
  loadSecurityScript(): Promise<string> {
    if (this.securityPromise) {
      return this.securityPromise;
    }
    this.securityPromise = new Promise<string>((resolve) => {
      const existing = this.getDeviceSessionId();
      if (existing) {
        resolve(existing);
        return;
      }
      const script = this.renderer.createElement('script') as HTMLScriptElement;
      script.type = 'text/javascript';
      script.src = MERCADOPAGO_SECURITY_URL;
      script.setAttribute('view', 'checkout');
      script.async = true;

      // MP populates the global asynchronously after the script's own load event, so resolving on
      // `onload` alone would hand back an empty id. Poll briefly instead, and give up rather than
      // hold the card form open.
      const started = Date.now();
      const poll = () => {
        const deviceId = this.getDeviceSessionId();
        if (deviceId || Date.now() - started > SECURITY_SCRIPT_TIMEOUT_MS) {
          resolve(deviceId);
          return;
        }
        setTimeout(poll, SECURITY_SCRIPT_POLL_MS);
      };
      script.onload = poll;
      script.onerror = () => {
        // Leave `securityPromise` set: a failed fetch is usually an ad-blocker, and retrying on
        // every card form would only repeat the same failure.
        console.error('Failed to load the MercadoPago security script.');
        resolve('');
      };

      this.renderer.appendChild(this.document.body, script);
    });
    return this.securityPromise;
  }

  /**
   * Gets the MercadoPago device session ID for fraud prevention.
   * This is set by the MercadoPago security.js script.
   *
   * @returns Device session ID or empty string if not available
   */
  getDeviceSessionId(): string {
    return window.MP_DEVICE_SESSION_ID ?? '';
  }

  // ==================== Error Handling ====================

  /**
   * Gets a user-friendly error message for a MercadoPago error code.
   *
   * @param code - MercadoPago error code
   * @returns User-friendly error message in Spanish
   */
  getErrorMessage(code: string): string {
    return MERCADOPAGO_ERROR_MESSAGES[code] ?? DEFAULT_ERROR_MESSAGE;
  }

  /**
   * Gets a field-specific error message for validation errors.
   *
   * @param field - Field name (e.g., "cardNumber", "securityCode")
   * @returns User-friendly error message for the field, or default message
   */
  getFieldErrorMessage(field: string): string {
    return FIELD_ERROR_MESSAGES[field] ?? DEFAULT_ERROR_MESSAGE;
  }

  /**
   * Processes a MercadoPago error and returns the appropriate user message.
   * Handles both code-based errors and field-based validation errors.
   *
   * @param error - MercadoPago error object
   * @returns User-friendly error message
   */
  processError(error: MercadoPagoError): string {
    if (error.code) {
      return this.getErrorMessage(error.code);
    }
    if (error.field) {
      return this.getFieldErrorMessage(error.field);
    }
    return DEFAULT_ERROR_MESSAGE;
  }

  /**
   * Checks if an error code is a known MercadoPago error.
   *
   * @param code - Error code to check
   * @returns true if the error code is recognized
   */
  isKnownError(code: string): boolean {
    return code in MERCADOPAGO_ERROR_MESSAGES;
  }

  // ==================== Cleanup ====================

  /**
   * Resets the service state. Useful for testing or re-initialization.
   * Note: This does not remove the loaded script from the DOM.
   */
  reset(): void {
    this.sdkInstance = null;
    this.scriptLoaded = false;
    this.scriptLoading = false;
    this.loadPromise = null;
  }
}
